const PRIMARY = ['FORM_12A', 'S_RAD', 'DEFECT_NOTICE'];

function toYmd(value) {
  if (!value) return null;
  const s = String(value).trim();
  const exact = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (exact) return `${exact[1]}-${exact[2]}-${exact[3]}`;
  const prefix = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (prefix) {
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const d = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return null;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addYearsYmd(ymd, years) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y + years, m - 1, d);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function inWindow(issued, prev, due, isFirst) {
  if (!issued || !due) return false;
  if (isFirst) return (!prev || issued >= prev) && issued <= due;
  return !!prev && issued > prev && issued <= due;
}

function blankPaperwork(row) {
  return {
    ...row,
    paperwork_type: null,
    paperwork_label: null,
    paperwork_short: null,
    document_id: null,
    document_name: null,
    record_id: null,
    issued_at: null,
    issued_date: null,
    locked: false,
    revision: null,
    is_defect: false,
    is_cert_lock: false,
    mail_status: '',
    mail_to: '',
    inspection_report_record_id: null,
    inspection_report_document_id: null,
    inspection_report_mail_status: '',
    inspection_report_mail_to: '',
    service_report_record_id: null,
    service_report_document_id: null,
    service_report_type: null,
    is_external: !!row?.is_external,
  };
}

function applyPeriodRecords(base, records) {
  const primary = [...records]
    .filter((r) => PRIMARY.includes(r.paperwork_type))
    .sort((a, b) => String(a.issued_at || '').localeCompare(String(b.issued_at || '')));
  const reports = records.filter((r) => r.paperwork_type === 'INSPECTION_REPORT');
  const services = records.filter(
    (r) => r.paperwork_type === 'SERVICE_REPORT' || r.paperwork_type === 'REINSPECTION_REPORT'
  );
  const form = primary.length ? primary[primary.length - 1] : null;
  const report = reports.length ? reports[reports.length - 1] : null;
  const service = services.length ? services[services.length - 1] : null;
  if (!form && !report && !service) return blankPaperwork(base);
  const next = blankPaperwork(base);
  if (form) {
    next.paperwork_type = form.paperwork_type;
    next.paperwork_label = form.paperwork_label || form.paperwork_type;
    next.record_id = form.id;
    next.document_id = form.document_id;
    next.issued_at = form.issued_at;
    next.issued_date = form.issued_at;
    next.revision = form.revision;
    next.locked = true;
    next.is_defect = form.paperwork_type === 'DEFECT_NOTICE';
    next.job_id = form.job_id || base.job_id;
    next.mail_status = form.mail_status || '';
    next.mail_to = form.mail_to || '';
    next.is_external =
      !!base.is_external ||
      String(form.amend_reason || '').toLowerCase() === 'external-register' ||
      /external/i.test(String(form.imr_notes || ''));
    if (form.worker_id) next.iqp_id = form.worker_id;
    if (form.worker_name) next.iqp_name = form.worker_name;
    if (form.paperwork_type === 'DEFECT_NOTICE') next.job_status = 'FAILED';
    else if (form.paperwork_type === 'FORM_12A' || form.paperwork_type === 'S_RAD') {
      next.job_status = 'COMPLETED';
    }
  }
  if (report) {
    next.inspection_report_record_id = report.id;
    next.inspection_report_document_id = report.document_id;
    next.inspection_report_mail_status = report.mail_status || '';
    next.inspection_report_mail_to = report.mail_to || '';
  }
  if (service) {
    next.service_report_record_id = service.id;
    next.service_report_document_id = service.document_id;
    next.service_report_type = service.paperwork_type;
  }
  return next;
}

/**
 * One large card per annual due since the contract / start date.
 * Latest year is first. Every SS is on every card; the form is only the one issued in that year.
 */
export function buildPaperworkYears({ cycleStart, totalYears, ssRows, history }) {
  const start = toYmd(cycleStart);
  const rows = Array.isArray(ssRows) ? ssRows : [];
  const past = Array.isArray(history) ? history : [];
  const count = Math.max(Number(totalYears) || 0, start ? 1 : 0);
  const n = start ? count : 1;
  const dues = [];
  if (start) {
    for (let i = 1; i <= n; i += 1) dues.push(addYearsYmd(start, i));
  } else {
    dues.push(null);
  }

  const periods = dues.map((due, idx) => {
    const prev = idx === 0 ? start : dues[idx - 1];
    const isFirst = idx === 0;
    const isLatest = idx === dues.length - 1;
    if (!due) {
      return {
        due,
        hasDefect: rows.some((r) => r.paperwork_type === 'DEFECT_NOTICE' || r.is_defect),
        ss: rows.map((base) => ({ ...base })),
      };
    }
    const ss = rows.map((base) => {
      const mine = past.filter(
        (h) =>
          String(h.ss_code) === String(base.ss_code) &&
          inWindow(toYmd(h.issued_at), prev, due, isFirst)
      );
      if (mine.length) return applyPeriodRecords(base, mine);
      if (!past.length && isLatest && (base.paperwork_type || base.document_id)) return { ...base };
      return blankPaperwork(base);
    });
    return {
      due,
      hasDefect: ss.some((r) => r.paperwork_type === 'DEFECT_NOTICE' || r.is_defect),
      ss,
    };
  });

  return periods.reverse();
}
